// CRM + engagement-lifecycle API surface. Pages import from here; never raw fetch.
import type { Language } from '../../shared/language.js';
import { request, upload } from './client.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Phase =
  | 'qualify' | 'discovery' | 'design' | 'contracting' | 'mobilise'
  | 'implement' | 'acceptance' | 'handover' | 'close';

export type Gate = 'G0' | 'G1' | 'G2' | 'G3' | 'G4' | 'G5' | 'G6' | 'G7' | 'G8';

export interface Client {
  id: number;
  /** Trading name — what the UI shows everywhere. */
  name: string;
  /** Contracting entity, printed on the SOW when it differs from the trading name. */
  legal_name: string | null;
  short_name: string | null;
  /** Uploaded branding mark (deck cover lockup) — served at /api/clients/:id/logo. */
  logo_key: string | null;
  sector: string | null;
  /** The correspondence language — every engagement of this client inherits it. */
  language: Language;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  postcode: string | null;
  /** ISO-3166 alpha-2. Also the dialling context for contact phone numbers. */
  country: string | null;
  /** DERIVED single line, maintained by the server. Read-only to the client. */
  address: string | null;
  notes: string | null;
  /** 'dormant' is legacy data only — the UI offers active/archived (DEVIATION: lean CRM). */
  status: 'active' | 'dormant' | 'archived';
  created_at: string;
  contact_count?: string;
  engagement_count?: string;
}

export interface Contact {
  id: number;
  client_id: number;
  first_name: string | null;
  last_name: string | null;
  /** DERIVED "first last", maintained by the server — display only. */
  name: string;
  role_title: string | null;
  email: string | null;
  /** As displayed (grouped international format). */
  phone: string | null;
  /** Strict E.164, or null when the number could not be understood. */
  phone_e164: string | null;
  is_sponsor: boolean;
  notes: string | null;
  created_at: string;
}

export type OpportunityStage = 'lead' | 'qualified' | 'proposal' | 'negotiation' | 'won' | 'lost';

/**
 * Opportunities are no longer a CRM sub-module (one-man shop — see the lean cut list). The
 * table, the routes and the history all stay; the only surviving surface is the client
 * record's single "Qualification note", which reads and writes the row gate G0 checks.
 */
export interface Opportunity {
  id: number;
  client_id: number;
  title: string;
  stage: OpportunityStage;
  value_estimate: string | null;
  currency: string;
  qualification_note: string | null;
  next_step: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientDetail extends Client {
  contacts: Contact[];
  opportunities: Opportunity[];
  engagements: Array<{
    id: number; code: string; name: string; current_phase: Phase;
    commercial_model: string; currency: string; archived_at: string | null; created_at: string;
  }>;
}

export interface Engagement {
  id: number;
  client_id: number;
  /** The counterpart who signs; also what satisfies gate G0's "named for this engagement". */
  sponsor_contact_id: number | null;
  /** Ballpark value agreed at qualification, before milestones exist (gate G2). */
  estimate_value: string | null;
  parent_engagement_id: number | null;
  name: string;
  code: string;
  current_phase: Phase;
  commercial_model: 'fixed' | 'tm' | 'capped_tm' | 'retainer';
  currency: string;
  /** The language of everything produced inside this engagement — inherited from the client, changeable here. */
  language: Language;
  review_window_days: number;
  kickoff_date: string | null;
  portal_enabled: boolean;
  archived_at: string | null;
  created_at: string;
  client_name: string;
  client_short_name: string | null;
  parent_code: string | null;
  parent_name: string | null;
  phase_label: string;
  phase_index: number;
  stage: number;
  gate: Gate;
  gate_title: string;
}

export interface RailStep {
  phase: Phase;
  label: string;
  gate: Gate;
  gate_title: string;
  stage: number;
}

export interface EngagementDetail extends Engagement {
  children: Array<{ id: number; code: string; name: string; current_phase: Phase }>;
  rail: RailStep[];
}

export interface GateCondition {
  label: string;
  kind: 'auto' | 'manual';
  met: boolean;
  detail: string;
  item_id?: number | null;
  note?: string | null;
  custom?: boolean;
}

export interface GateSummaryRow {
  gate: Gate;
  phase: Phase;
  title: string;
  allMet: boolean;
  metCount: number;
  total: number;
  stage: number;
}

export interface GateView {
  engagement_id: number;
  current_phase: Phase;
  current_gate: Gate;
  gate: Gate;
  phase: Phase;
  title: string;
  conditions: GateCondition[];
  allMet: boolean;
  metCount: number;
  total: number;
  all: GateSummaryRow[];
  rail: RailStep[];
}

export interface GateLamp {
  engagement_id: number;
  gate: Gate;
  title: string;
  allMet: boolean;
  metCount: number;
  total: number;
  unmet: string[];
}

export interface DashboardStats {
  clients: number;
  clients_total: number;
  active_engagements: number;
  engagements_total: number;
  pending_proposals: number;
  open_defects: number;
  open_blocking_defects: number;
  /** Issued ARs whose deadline falls inside window_days — drives the warn colour. */
  review_windows_expiring: number;
  /** Every issued AR with a deadline, whatever the horizon — drives the readout. */
  review_windows_open: number;
  open_tasks: number;
  documents_processing: number;
  window_days: number;
  expiring_reviews: Array<{
    id: number; ref: string; review_deadline: string; status: string;
    engagement_id: number; code: string; name: string; days_left: number;
  }>;
}

export interface PhaseTransition {
  id: number;
  engagement_id: number;
  from_phase: Phase;
  to_phase: Phase;
  forced: boolean;
  override_reason: string | null;
  gate_snapshot: {
    gate: Gate; title: string; allMet: boolean; metCount: number; total: number;
    conditions: Array<{ label: string; kind: 'auto' | 'manual'; met: boolean; detail: string }>;
    evaluated_at: string;
  } | null;
  created_by: number | null;
  created_at: string;
  actor_name?: string | null;
  actor_email?: string | null;
}

export type TimelineEvent =
  | {
      kind: 'transition'; id: string; at: string; from_phase: Phase; to_phase: Phase;
      forward: boolean; forced: boolean; override_reason: string | null;
      gate_snapshot: PhaseTransition['gate_snapshot'];
      actor_name: string | null; actor_email: string | null; stage: number; label: string;
    }
  | {
      kind: 'audit'; id: string; at: string; action: string; resource_type: string;
      resource_id: string | null; details: Record<string, unknown> | null;
      actor_name: string | null; actor_email: string | null; label: string;
    };

export interface Timeline {
  engagement_id: number;
  transitions: PhaseTransition[];
  events: TimelineEvent[];
}

export interface PhaseMeta {
  phases: RailStep[];
  gates: Array<{ gate: Gate; title: string; manual_defaults: string[] }>;
  commercial_models: string[];
}

export type AppSettings = Record<string, string>;

// ---------------------------------------------------------------------------
// Deletion (utils/deletion.ts is the server-side authority; this is its wire shape)
// ---------------------------------------------------------------------------

/**
 * RETIRE and DELETE coexist. Archive/descope/supersede keep a real thing and its history;
 * delete removes a mistake. A plan answers "may this go, what goes with it, and if not why
 * not" — and a blocker always names the alternative, so a disabled button never stands alone.
 */
export interface Blocker {
  code: string;
  message: string;
}

export interface DeletionPlan {
  allowed: boolean;
  blockers: Blocker[];
  cascade: Array<{ what: string; count: number }>;
  label: string;
  entity?: string;
  id?: number;
}

export interface ClientDeletionPlan extends DeletionPlan {
  engagements: Array<{ id: number; code: string; name: string }>;
}

export interface EngagementDeletionPlan extends DeletionPlan {
  code: string | null;
  /** What the operator must type to confirm — the engagement code. */
  confirm_token: string | null;
  archived: boolean;
  /** Rows that survive but lose a pointer, named so the operator is not surprised. */
  detaches: Array<{ what: string; count: number }>;
  children: Array<{ id: number; code: string; name: string }>;
  workspace_busy: boolean;
}

export interface DeletionResult {
  ok: boolean;
  deleted: number;
  cascade: Array<{ what: string; count: number }>;
  files?: {
    workspaces_removed: string[]; workspaces_kept: string[];
    blobs_removed: number; blobs_failed: number;
  };
}

// ---------------------------------------------------------------------------
// Clients / contacts / opportunities
// ---------------------------------------------------------------------------

const qs = (params: Record<string, string | number | undefined | null>) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export const listClients = (opts: { status?: string; q?: string } = {}) =>
  request<Client[]>(`/clients${qs(opts)}`);

export const getClient = (id: number) => request<ClientDetail>(`/clients/${id}`);

export const createClient = (body: Partial<Client>) =>
  request<Client>('/clients', { method: 'POST', body: JSON.stringify(body) });

export const updateClient = (id: number, body: Partial<Client>) =>
  request<Client>(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(body) });

/** Retire: the client stays, with its history, and leaves the active list. */
export const archiveClient = (id: number) => request<Client>(`/clients/${id}`, { method: 'DELETE' });

export const uploadClientLogo = (id: number, file: File) => {
  const form = new FormData();
  form.append('file', file);
  return upload<Client>(`/clients/${id}/logo`, form);
};

export const removeClientLogo = (id: number) =>
  request<{ ok: boolean }>(`/clients/${id}/logo`, { method: 'DELETE' });

/** What deleting this client would take with it, and why it might refuse. */
export const clientDeletionPlan = (id: number) => request<ClientDeletionPlan>(`/clients/${id}/deletion-plan`);

/** Delete: the client was a mistake. Separate path from archive on purpose. */
export const deleteClient = (id: number) =>
  request<DeletionResult>(`/clients/${id}/hard`, { method: 'DELETE' });

/**
 * Classify the client's whole library — folder, tags, one-line summary per document.
 * The endpoint lands with the Librarian phase; until then this returns 404 and the
 * button should say so honestly rather than pretend.
 */
export const runLibrarian = (clientId: number) =>
  request<{ ok: boolean; queued?: number; classified?: number; notice?: string }>(
    `/clients/${clientId}/librarian`, { method: 'POST', body: JSON.stringify({}) },
  );

export const createContact = (clientId: number, body: Partial<Contact>) =>
  request<Contact>(`/clients/${clientId}/contacts`, { method: 'POST', body: JSON.stringify(body) });

export const updateContact = (clientId: number, id: number, body: Partial<Contact>) =>
  request<Contact>(`/clients/${clientId}/contacts/${id}`, { method: 'PUT', body: JSON.stringify(body) });

export const contactDeletionPlan = (clientId: number, id: number) =>
  request<DeletionPlan>(`/clients/${clientId}/contacts/${id}/deletion-plan`);

export const deleteContact = (clientId: number, id: number) =>
  request<void>(`/clients/${clientId}/contacts/${id}`, { method: 'DELETE' });

export const createOpportunity = (clientId: number, body: Partial<Opportunity>) =>
  request<Opportunity>(`/clients/${clientId}/opportunities`, { method: 'POST', body: JSON.stringify(body) });

export const updateOpportunity = (clientId: number, id: number, body: Partial<Opportunity>) =>
  request<Opportunity>(`/clients/${clientId}/opportunities/${id}`, { method: 'PUT', body: JSON.stringify(body) });

export const deleteOpportunity = (clientId: number, id: number) =>
  request<void>(`/clients/${clientId}/opportunities/${id}`, { method: 'DELETE' });

// ---------------------------------------------------------------------------
// Engagements + lifecycle
// ---------------------------------------------------------------------------

export const listEngagements = (opts: { archived?: boolean; client_id?: number } = {}) =>
  request<Engagement[]>(`/engagements${qs({ archived: opts.archived ? 1 : undefined, client_id: opts.client_id })}`);

export const getEngagement = (id: number) => request<EngagementDetail>(`/engagements/${id}`);

export const createEngagement = (body: {
  client_id: number; name: string; code?: string; commercial_model?: string;
  currency?: string; language?: string; review_window_days?: number; parent_engagement_id?: number | null; kickoff_date?: string | null;
  sponsor_contact_id?: number | null; estimate_value?: number | null;
}) => request<Engagement>('/engagements', { method: 'POST', body: JSON.stringify(body) });

/** Contacts of one client — the sponsor picker's options. */
export const getClientContacts = (clientId: number) =>
  request<Contact[]>(`/clients/${clientId}/contacts`);

export const updateEngagement = (id: number, body: Record<string, unknown>) =>
  request<Engagement>(`/engagements/${id}`, { method: 'PUT', body: JSON.stringify(body) });

/** Retire: the engagement ended. Everything stays; it leaves the active list. */
export const archiveEngagement = (id: number) => request<Engagement>(`/engagements/${id}`, { method: 'DELETE' });

/** What deleting this engagement would take with it — documents, requirements, runs, the lot. */
export const engagementDeletionPlan = (id: number) =>
  request<EngagementDeletionPlan>(`/engagements/${id}/deletion-plan`);

/**
 * Delete: the engagement was a mistake. Its own verb-path, because DELETE /engagements/:id
 * archives and muscle memory must never obliterate an engagement by accident.
 */
export const deleteEngagement = (id: number) =>
  request<DeletionResult & { code: string }>(`/engagements/${id}/hard`, { method: 'DELETE' });

export const getPhaseMeta = () => request<PhaseMeta>('/engagements/meta/phases');

export const getDashboardStats = (windowDays?: number) =>
  request<DashboardStats>(`/engagements/stats/dashboard${qs({ window_days: windowDays })}`);

export const getGateLamps = () => request<GateLamp[]>('/engagements/gates/summary');

export const getGates = (engagementId: number, gate?: Gate) =>
  request<GateView>(`/engagements/${engagementId}/gates${qs({ gate })}`);

/**
 * Tick, untick and annotate in ONE request. `checked` omitted = leave the tick alone (the
 * server then records the edit as gate.item.note, not gate.item.uncheck).
 */
export const setGateItem = (engagementId: number, gate: Gate, body: { label: string; checked?: boolean; note?: string | null }) =>
  request<{ id: number }>(`/engagements/${engagementId}/gates/${gate}/items`, { method: 'PUT', body: JSON.stringify(body) });

export const addGateItem = (engagementId: number, gate: Gate, body: { label: string; note?: string | null }) =>
  request<{ id: number }>(`/engagements/${engagementId}/gates/${gate}/items`, { method: 'POST', body: JSON.stringify(body) });

export const deleteGateItem = (engagementId: number, gate: Gate, itemId: number) =>
  request<void>(`/engagements/${engagementId}/gates/${gate}/items/${itemId}`, { method: 'DELETE' });

export const transition = (
  engagementId: number,
  body: { to_phase: Phase; force?: boolean; override_reason?: string },
) => request<{ transition: PhaseTransition; engagement: Engagement }>(
  `/engagements/${engagementId}/transition`,
  { method: 'POST', body: JSON.stringify(body) },
);

export const getTimeline = (engagementId: number, limit = 100) =>
  request<Timeline>(`/engagements/${engagementId}/timeline${qs({ limit })}`);

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export const getSettings = () => request<AppSettings>('/settings');
export const updateSettings = (body: AppSettings) =>
  request<AppSettings>('/settings', { method: 'PUT', body: JSON.stringify(body) });

// ---------------------------------------------------------------------------
// Shared presentation helpers (phase rail / stage spectrum)
// ---------------------------------------------------------------------------

export const PHASE_ORDER: Phase[] = [
  'qualify', 'discovery', 'design', 'contracting', 'mobilise',
  'implement', 'acceptance', 'handover', 'close',
];

export const PHASE_LABELS: Record<Phase, string> = {
  qualify: 'Qualify', discovery: 'Discovery', design: 'Design', contracting: 'Contracting',
  mobilise: 'Mobilise', implement: 'Implement', acceptance: 'Acceptance',
  handover: 'Handover', close: 'Close',
};

/** Stage spectrum position: qualify=1 … handover/close=7. */
export const PHASE_STAGE: Record<Phase, number> = {
  qualify: 1, discovery: 2, design: 3, contracting: 4,
  mobilise: 5, implement: 5, acceptance: 6, handover: 7, close: 7,
};

export const PHASE_GATES: Record<Phase, Gate> = {
  qualify: 'G0', discovery: 'G1', design: 'G2', contracting: 'G3', mobilise: 'G4',
  implement: 'G5', acceptance: 'G6', handover: 'G7', close: 'G8',
};

export const COMMERCIAL_MODEL_LABELS: Record<string, string> = {
  fixed: 'Fixed price', tm: 'Time & materials', capped_tm: 'Capped T&M', retainer: 'Retainer',
};

/**
 * ATTEST LAW 2 — a phase is a POSITION on a path, not a colour to memorise. These two
 * helpers used to index Meridian's seven-hue stage spectrum; they now resolve to the one
 * neutral pair for every step, and the rail carries position with WEIGHT instead (the
 * current step is full ink and taller, passed steps are muted, future steps are hairline).
 * They survive so no call site can quietly reintroduce a category hue.
 */
export const stageVar = (_n?: number) => 'var(--ink-3)';
export const stageDimVar = (_n?: number) => 'var(--well)';

/** 'YYYY-MM-DD HH:MM:SS+00' | ISO → 'YYYY-MM-DD HH:MM' */
export function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  return String(v).replace('T', ' ').slice(0, 16);
}
export function fmtDate(v: string | null | undefined): string {
  if (!v) return '—';
  return String(v).slice(0, 10);
}
export function fmtMoney(v: string | number | null | undefined, currency = 'CHF'): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${currency} ${n.toLocaleString('de-CH', { maximumFractionDigits: 0 })}`;
}
